import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, fetchProducts, API } from "../lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, LogOut, X, ChevronRight, ChevronLeft, Upload, RotateCcw, KeyRound, Eye, EyeOff, Video, Images, ArrowUp, ArrowDown, GripVertical, Mail, Send } from "lucide-react";
import { CATEGORIES } from "../constants/data";
import AdminTrash from "./admin/AdminTrash";
import AdminCustomTiles from "./admin/AdminCustomTiles";
import AdminContacts from "./admin/AdminContacts";
import AdminReviews from "./admin/AdminReviews";
import AdminCoupons from "./admin/AdminCoupons";
import AdminOrders from "./admin/AdminOrders";
import AdminAbandoned from "./admin/AdminAbandoned";
import AdminNotifySignups from "./admin/AdminNotifySignups";
import AdminTiles from "./admin/AdminTiles";
import AdminCatalogues from "./admin/AdminCatalogues";
import ProductRow from "./admin/ProductRow";
import ProductCardMobile from "./admin/ProductCardMobile";
import { bulkDelete } from "./admin/_shared";
import { refreshCategoryImages } from "../hooks/useCategoryImages";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

// Per-kind upload limits — keep in sync with backend (server.py update_catalogue caps)
const KIND_LIMITS = {
  catalogue: { images: 4, videos: 0 },
  combo: { images: 10, videos: 4 },
  trousseau: { images: 12, videos: 4 },
};

const empty = { name: "", category: "her", categories: ["her"], price: 0, mrp: 0, description: "", short_description: "", product_info: "", why_hamper: "", stock: 0, images: [""], variants: [], color_name: "", color_hex: "#FFE7F1", featured: false, tags: [] };

export default function AdminDashboard() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [pwForm, setPwForm] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [pwShow, setPwShow] = useState({ old: false, new: false, confirm: false });
  const [pwBusy, setPwBusy] = useState(false);
  const [testEmail, setTestEmail] = useState({ to: "", busy: false });
  // Product form image selection (for bulk delete) and drag state (for reorder)
  const [formImgSel, setFormImgSel] = useState(new Set());
  const [formImgDrag, setFormImgDrag] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("nc_admin_token");
  useEffect(() => { if (!token) navigate("/admin/login"); }, [token, navigate]);

  const reloadProducts = () => fetchProducts().then(setProducts);

  const bulkDeleteProducts = () => bulkDelete("/admin/products/delete-bulk", Array.from(selectedProducts), reloadProducts, setSelectedProducts, "products");

  useEffect(() => {
    if (tab === "products") reloadProducts();
  }, [tab]);

  const logout = () => { localStorage.removeItem("nc_admin_token"); navigate("/admin/login"); };

  const startEdit = (p) => {
    const cats = p.categories && p.categories.length ? p.categories : [p.category].filter(Boolean);
    setEditing(p);
    setForm({ ...p, category: cats[0], categories: cats, tags: p.tags || [], variants: p.variants || [] });
    setFormImgSel(new Set());
    setFormImgDrag(null);
  };
  const startNew = () => { setEditing("new"); setForm(empty); setFormImgSel(new Set()); setFormImgDrag(null); };
  const cancel = () => { setEditing(null); setForm(empty); setFormImgSel(new Set()); setFormImgDrag(null); };

  const [formUploadProgress, setFormUploadProgress] = useState({ done: 0, total: 0 });
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Client-side size filter (keeps user's error toasts predictable)
    const valid = files.filter((f) => {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} > 5MB — skipped`); return false; }
      return true;
    });
    if (!valid.length) return;

    // 1) Optimistic previews via object URLs — visible instantly
    const previews = valid.map((f) => URL.createObjectURL(f));
    const startLen = (form.images || []).filter(Boolean).length;
    setForm((f) => ({ ...f, images: [...(f.images || []).filter(Boolean), ...previews] }));
    setFormUploadProgress({ done: 0, total: valid.length });

    // 2) Upload all files in PARALLEL and swap URLs as each finishes
    let done = 0;
    const swap = (blobUrl, realUrl) => {
      setForm((f) => ({
        ...f,
        images: (f.images || []).map((u) => (u === blobUrl ? realUrl : u)),
      }));
      URL.revokeObjectURL(blobUrl);
    };
    await Promise.all(valid.map(async (file, i) => {
      const previewUrl = previews[i];
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await adminApi().post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        swap(previewUrl, `${API}${res.data.url.replace(/^\/api/, "")}`);
      } catch {
        // Fall back to base64 so the image still saves with the product
        try {
          const b64 = await new Promise((r) => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(file); });
          swap(previewUrl, b64);
        } catch {
          toast.error(`Upload failed for ${file.name}`);
          // Remove failed preview from form
          setForm((f) => ({ ...f, images: (f.images || []).filter((u) => u !== previewUrl) }));
        }
      } finally {
        done += 1;
        setFormUploadProgress((prev) => ({ ...prev, done }));
      }
    }));
    toast.success(`${valid.length} image${valid.length > 1 ? "s" : ""} added (position ${startLen + 1}–${startLen + valid.length})`);
    // Clear progress after a short delay so shopper sees the "done" state briefly
    setTimeout(() => setFormUploadProgress({ done: 0, total: 0 }), 900);
  };

  const removeImage = (i) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
    // Shift selected indices that come after the removed one
    setFormImgSel((prev) => {
      const next = new Set();
      prev.forEach((idx) => { if (idx < i) next.add(idx); else if (idx > i) next.add(idx - 1); });
      return next;
    });
  };

  const toggleFormImgSelected = (i) => {
    setFormImgSel((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const bulkDeleteFormImages = () => {
    if (formImgSel.size === 0) return;
    if (!window.confirm(`Remove ${formImgSel.size} selected image${formImgSel.size > 1 ? "s" : ""}?`)) return;
    setForm((f) => ({ ...f, images: (f.images || []).filter((_, idx) => !formImgSel.has(idx)) }));
    setFormImgSel(new Set());
  };

  const reorderFormImage = (from, to) => {
    setForm((f) => {
      const arr = (f.images || []).filter(Boolean);
      if (to < 0 || to >= arr.length || from === to) return f;
      const next = [...arr];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return { ...f, images: next };
    });
    setFormImgSel(new Set()); // clear stale selections after reorder
  };

  // ----- Product variants (colour options) -----
  const genVariantId = () => `var_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

  const addVariant = () => {
    setForm((f) => ({
      ...f,
      variants: [
        ...(f.variants || []),
        { id: genVariantId(), color_name: "", color_hex: "#FFE7F1", images: [], stock: 0, price: null, short_description: "", description: "", product_info: "", why_hamper: "", tags: [] },
      ],
    }));
  };

  const updateVariant = (vid, patch) => {
    setForm((f) => ({
      ...f,
      variants: (f.variants || []).map((v) => (v.id === vid ? { ...v, ...patch } : v)),
    }));
  };

  const removeVariant = (vid) => {
    if (!window.confirm("Remove this colour variant?")) return;
    setForm((f) => ({ ...f, variants: (f.variants || []).filter((v) => v.id !== vid) }));
  };

  const uploadVariantImages = async (vid, files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const uploaded = [];
    for (const file of list) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} > 5MB — skipped`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await adminApi().post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${API}${res.data.url.replace(/^\/api/, "")}`);
      } catch { /* ignore */ }
    }
    if (!uploaded.length) return;
    setForm((f) => ({
      ...f,
      variants: (f.variants || []).map((v) =>
        v.id === vid ? { ...v, images: [...(v.images || []), ...uploaded].slice(0, 6) } : v
      ),
    }));
    toast.success(`${uploaded.length} image(s) added to variant`);
  };

  const removeVariantImage = (vid, idx) => {
    setForm((f) => ({
      ...f,
      variants: (f.variants || []).map((v) =>
        v.id === vid ? { ...v, images: (v.images || []).filter((_, i) => i !== idx) } : v
      ),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    const cats = (form.categories && form.categories.length ? form.categories : [form.category]).filter(Boolean);
    if (cats.length === 0) {
      toast.error("Pick at least one category");
      return;
    }
    try {
      const cleanVariants = (form.variants || [])
        .filter((v) => (v.color_name || "").trim())
        .map((v) => {
          const priceRaw = v.price;
          const price = priceRaw === "" || priceRaw === null || priceRaw === undefined
            ? null
            : (Number.isFinite(Number(priceRaw)) ? Number(priceRaw) : null);
          const tagsIn = v.tags;
          const tags = Array.isArray(tagsIn)
            ? tagsIn
            : (typeof tagsIn === "string" ? tagsIn.split(",").map((t) => t.trim()).filter(Boolean) : []);
          return {
            id: v.id,
            color_name: v.color_name.trim(),
            color_hex: v.color_hex || "#FFE7F1",
            images: (v.images || []).filter(Boolean),
            stock: Number(v.stock) || 0,
            price,
            short_description: (v.short_description || "").trim(),
            description: (v.description || "").trim(),
            product_info: (v.product_info || "").trim(),
            why_hamper: (v.why_hamper || "").trim(),
            tags,
          };
        });
      const payload = {
        ...form,
        category: cats[0],
        categories: cats,
        price: Number(form.price),
        mrp: Number(form.mrp) || null,
        stock: Number(form.stock),
        images: form.images.filter(Boolean),
        variants: cleanVariants,
        color_name: (form.color_name || "").trim(),
        color_hex: form.color_hex || "#FFE7F1",
        tags: typeof form.tags === "string" ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : form.tags
      };
      if (editing === "new") {
        await adminApi().post("/admin/products", payload);
        toast.success("Product created");
      } else {
        await adminApi().put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      }
      cancel();
      reloadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Move "${p.name}" to Recently Deleted?\n\nYou can restore it any time within 30 days from the "Recently Deleted" tab. After that it will be permanently removed.`)) return;
    try { await adminApi().delete(`/admin/products/${p.id}`); toast.success(`Moved "${p.name}" to Recently Deleted`); reloadProducts(); }
    catch { toast.error("Delete failed"); }
  };

  // Reorder products via ↑ / ↓ arrows or drag-and-drop (persistProductOrder is shared)
  const [reordering, setReordering] = useState(false);
  const persistProductOrder = async (nextList) => {
    setProducts(nextList);
    try {
      await adminApi().post("/admin/products/reorder", { ids: nextList.map((p) => p.id) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reorder failed");
      reloadProducts();
    }
  };
  const moveProduct = async (index, dir) => {
    if (reordering) return;
    const target = index + dir;
    if (target < 0 || target >= products.length) return;
    setReordering(true);
    const next = products.slice();
    [next[index], next[target]] = [next[target], next[index]];
    await persistProductOrder(next);
    setReordering(false);
  };

  // dnd-kit sensors: pointer (mouse) with a tiny distance threshold so clicks don't
  // start a drag; touch with a small delay so vertical scrolls still work on mobile.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onProductDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = products.findIndex((p) => p.id === active.id);
    const to = products.findIndex((p) => p.id === over.id);
    if (from < 0 || to < 0) return;
    persistProductOrder(arrayMove(products, from, to));
  };

  const changePassword = async (e) => {
    e.preventDefault();
    const { old_password, new_password, confirm_password } = pwForm;
    if (!old_password || !new_password) { toast.error("All fields are required"); return; }
    if (new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (new_password !== confirm_password) { toast.error("New password and confirmation do not match"); return; }
    if (old_password === new_password) { toast.error("New password must be different from current"); return; }
    setPwBusy(true);
    try {
      await adminApi().post("/admin/change-password", { old_password, new_password });
      toast.success("Password changed. Please log in again with the new password.");
      setPwForm({ old_password: "", new_password: "", confirm_password: "" });
      // Force re-login so the new password is used
      setTimeout(() => { localStorage.removeItem("nc_admin_token"); navigate("/admin/login"); }, 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setPwBusy(false);
    }
  };

  const sendTestEmail = async (e) => {
    e.preventDefault();
    const to = (testEmail.to || "").trim();
    if (!to || !to.includes("@")) { toast.error("Please enter a valid recipient email"); return; }
    setTestEmail((v) => ({ ...v, busy: true }));
    try {
      const r = await adminApi().post("/admin/test-email", { to });
      toast.success(`Test email sent to ${r.data?.to || to} — check inbox in ~30s`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test email failed — check RESEND_API_KEY on this backend");
    } finally {
      setTestEmail((v) => ({ ...v, busy: false }));
    }
  };

  return (
    <div className="pt-24 sm:pt-28 pb-20" data-testid="admin-dashboard">
      <div className="container-x">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="label-small">Admin</p>
            <h1 className="font-serif text-2xl sm:text-4xl text-[#2D2424] mt-1 sm:mt-2">Dashboard</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {tab === "products" && (
              <button onClick={startNew} data-testid="admin-new-product" className="btn-primary text-xs sm:text-sm px-3 sm:px-5 py-2 whitespace-nowrap">
                <Plus className="w-4 h-4"/><span className="hidden xs:inline"> Add </span>Product
              </button>
            )}
            <button onClick={logout} data-testid="admin-logout" className="btn-secondary text-xs sm:text-sm px-3 sm:px-5 py-2 whitespace-nowrap">
              <LogOut className="w-4 h-4"/><span className="hidden xs:inline"> Logout</span>
            </button>
          </div>
        </div>

        <div className="mt-5 sm:mt-6 -mx-4 sm:mx-0 border-b border-[#E5DBD8] overflow-x-auto scrollbar-thin scrollbar-thumb-[#E5DBD8]">
          <div className="flex gap-1 sm:gap-2 px-4 sm:px-0 min-w-max">
          {["products", "tiles", "custom-tiles", "catalogues", "orders", "contacts", "notify-signups", "coupons", "reviews", "abandoned", "recently-deleted", "settings"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`px-3 sm:px-5 py-2.5 sm:py-3 text-[10px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase whitespace-nowrap flex-shrink-0 ${tab === t ? "border-b-2 border-[#7A1F2B] text-[#7A1F2B]" : "text-[#5C5555] hover:text-[#7A1F2B]"}`}
            >{t}</button>
          ))}
          </div>
        </div>

        {tab === "products" && (
        <div className="mt-6">
          {products.length > 0 && (
            <p className="text-[11px] text-[#5C5555] mb-2 font-hand italic">
              💡 Drag the <GripVertical className="inline w-3.5 h-3.5 align-text-bottom text-[#7A1F2B]"/> handle to reorder — works with mouse, touch (long-press) &amp; keyboard. Or use the ↑/↓ arrows.
            </p>
          )}
          {products.length > 0 && (
            <div className="flex items-center justify-between flex-wrap gap-2 bg-[#F2E8E4]/50 border border-[#E5DBD8] px-3 py-2 mb-3 rounded">
              <label className="inline-flex items-center gap-2 text-sm font-hand cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedProducts.size > 0 && selectedProducts.size === products.length}
                  onChange={(e) => setSelectedProducts(e.target.checked ? new Set(products.map((p) => p.id)) : new Set())}
                  data-testid="products-select-all"
                  className="w-4 h-4 accent-[#7A1F2B] cursor-pointer"
                />
                Select all
              </label>
              <div className="text-xs text-[#5C5555]">{selectedProducts.size} selected</div>
              <div className="flex gap-2">
                {selectedProducts.size > 0 && (
                  <button type="button" onClick={() => setSelectedProducts(new Set())} className="text-xs px-3 py-1.5 border border-[#E5DBD8] hover:bg-white rounded">Clear</button>
                )}
                <button
                  type="button"
                  disabled={selectedProducts.size === 0}
                  onClick={bulkDeleteProducts}
                  data-testid="products-bulk-delete"
                  className="text-xs px-3 py-1.5 bg-[#7A1F2B] text-white rounded hover:bg-[#5C1520] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5"/> Delete selected
                </button>
              </div>
            </div>
          )}
          <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onProductDragEnd}>
            <SortableContext items={products.map((p) => p.id)} strategy={verticalListSortingStrategy}>
              <div className="overflow-x-auto hidden sm:block">
                <table className="w-full border border-[#E5DBD8] bg-white text-sm">
                  <thead className="bg-[#F2E8E4] text-[#2D2424] text-left">
                    <tr>
                      <th className="p-3 w-8"></th>
                      <th className="p-3 w-10"></th>
                      <th className="p-3 w-24">Order</th>
                      <th className="p-3">Image</th>
                      <th className="p-3">Name</th>
                      <th className="p-3">Category</th>
                      <th className="p-3">Price</th>
                      <th className="p-3">Stock</th>
                      <th className="p-3">Featured</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, idx) => (
                      <ProductRow
                        key={p.id}
                        product={p}
                        idx={idx}
                        total={products.length}
                        checked={selectedProducts.has(p.id)}
                        onCheck={(id, on) => setSelectedProducts((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; })}
                        onEdit={startEdit}
                        onDelete={remove}
                        onMoveUp={(i) => moveProduct(i, -1)}
                        onMoveDown={(i) => moveProduct(i, 1)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card layout (< sm) */}
              <div className="sm:hidden flex flex-col gap-2.5">
                {products.map((p, idx) => (
                  <ProductCardMobile
                    key={p.id}
                    product={p}
                    idx={idx}
                    checked={selectedProducts.has(p.id)}
                    onCheck={(id, on) => setSelectedProducts((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; })}
                    onEdit={startEdit}
                    onDelete={remove}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
        )}


        {tab === "tiles" && <AdminTiles />}
        {tab === "custom-tiles" && <AdminCustomTiles />}



        {tab === "catalogues" && <AdminCatalogues />}
        {tab === "orders" && <AdminOrders />}
        {tab === "contacts" && <AdminContacts />}
        {tab === "coupons" && <AdminCoupons />}
        {tab === "reviews" && <AdminReviews />}
        {tab === "abandoned" && <AdminAbandoned />}
        {tab === "notify-signups" && <AdminNotifySignups />}
        {tab === "recently-deleted" && <AdminTrash />}

        {tab === "settings" && (
          <div className="mt-6 max-w-xl space-y-6" data-testid="admin-settings-tab">
            {/* Email pipe test */}
            <div className="bg-white border border-[#E5DBD8] rounded-lg p-6" data-testid="admin-test-email-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#EAFBE7] border-2 border-[#2D6A4F] flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#2D6A4F]"/>
                </div>
                <div>
                  <h2 className="font-serif text-2xl text-[#2D2424] leading-tight">Email pipeline</h2>
                  <p className="text-xs text-[#5C5555]">Verify that Resend + your <code>hello@naathicharami.com</code> domain are wired for order confirmations.</p>
                </div>
              </div>
              <form onSubmit={sendTestEmail} className="mt-5 flex flex-col sm:flex-row gap-2 items-stretch">
                <input
                  type="email"
                  value={testEmail.to}
                  onChange={(e) => setTestEmail({ ...testEmail, to: e.target.value })}
                  placeholder="you@example.com"
                  data-testid="admin-test-email-input"
                  required
                  className="flex-1 border border-[#E5DBD8] px-3 py-2 rounded focus:outline-none focus:border-[#7A1F2B]"
                />
                <button
                  type="submit"
                  disabled={testEmail.busy}
                  data-testid="admin-test-email-submit"
                  className="btn-primary shrink-0 disabled:opacity-60"
                >
                  <Send className="w-4 h-4"/> {testEmail.busy ? "Sending…" : "Send test"}
                </button>
              </form>
              <p className="mt-2 text-[11px] text-[#9B9494]">
                If it doesn&apos;t land within a minute, check spam &mdash; and confirm <b>hello@naathicharami.com</b> is verified in your Resend dashboard.
              </p>
            </div>

            <div className="bg-white border border-[#E5DBD8] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#FFE7F1] border-2 border-[#7A1F2B] flex items-center justify-center">
                  <KeyRound className="w-4 h-4 text-[#7A1F2B]"/>
                </div>
                <div>
                  <h2 className="font-serif text-2xl text-[#2D2424] leading-tight">Change Admin Password</h2>
                  <p className="text-xs text-[#5C5555]">You&apos;ll be signed out and asked to log in again after a successful change.</p>
                </div>
              </div>
              <form onSubmit={changePassword} className="mt-5 space-y-4" data-testid="admin-change-password-form">
                {[
                  { key: "old_password", label: "Current Password", showKey: "old", testid: "pw-old" },
                  { key: "new_password", label: "New Password (min 6 chars)", showKey: "new", testid: "pw-new" },
                  { key: "confirm_password", label: "Confirm New Password", showKey: "confirm", testid: "pw-confirm" },
                ].map((f) => (
                  <label key={f.key} className="block">
                    <span className="text-xs uppercase tracking-wider text-[#5C5555]">{f.label}</span>
                    <div className="relative mt-1">
                      <input
                        type={pwShow[f.showKey] ? "text" : "password"}
                        value={pwForm[f.key]}
                        onChange={(e) => setPwForm({ ...pwForm, [f.key]: e.target.value })}
                        data-testid={f.testid}
                        required
                        autoComplete={f.key === "old_password" ? "current-password" : "new-password"}
                        className="w-full border border-[#E5DBD8] px-3 py-2 pr-10 rounded focus:outline-none focus:border-[#7A1F2B]"
                      />
                      <button
                        type="button"
                        onClick={() => setPwShow({ ...pwShow, [f.showKey]: !pwShow[f.showKey] })}
                        data-testid={`${f.testid}-toggle`}
                        className="absolute inset-y-0 right-2 my-auto w-7 h-7 flex items-center justify-center text-[#5C5555] hover:text-[#7A1F2B]"
                        aria-label="toggle visibility"
                      >
                        {pwShow[f.showKey] ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                      </button>
                    </div>
                  </label>
                ))}
                <button
                  type="submit"
                  disabled={pwBusy}
                  data-testid="admin-change-password-submit"
                  className="btn-primary w-full sm:w-auto disabled:opacity-60"
                >
                  {pwBusy ? "Saving…" : "Update Password"}
                </button>
              </form>
            </div>
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4" onClick={cancel}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white p-4 sm:p-6 max-w-2xl w-full max-h-[92vh] overflow-y-auto rounded-lg" data-testid="admin-edit-modal">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="font-serif text-xl sm:text-2xl">{editing === "new" ? "New Product" : "Edit Product"}</h2>
                <button type="button" onClick={cancel} className="p-1 -mr-1"><X className="w-5 h-5"/></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <label className="sm:col-span-2"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Name</span>
                  <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="form-name" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2 focus:outline-none focus:border-[#7A1F2B]"/>
                </label>
                <div className="sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-[#5C5555]">Categories <span className="text-[#7A1F2B] normal-case">(stock shared across all selected categories)</span></span>
                  <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border border-[#E5DBD8] rounded bg-[#F2E8E4]/30" data-testid="form-categories-multi">
                    {CATEGORIES.map((c) => {
                      const cats = form.categories || [];
                      const checked = cats.includes(c.slug);
                      return (
                        <label key={c.slug} className={`flex items-center gap-2 px-3 py-2 border rounded cursor-pointer text-sm transition-colors ${checked ? "bg-[#FFE7F1] border-[#7A1F2B] text-[#7A1F2B]" : "bg-white border-[#E5DBD8] hover:border-[#7A1F2B]"}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const cur = new Set(form.categories || []);
                              if (e.target.checked) cur.add(c.slug); else cur.delete(c.slug);
                              const next = Array.from(cur);
                              setForm({ ...form, categories: next, category: next[0] || form.category });
                            }}
                            data-testid={`form-category-${c.slug}`}
                            className="w-4 h-4 accent-[#7A1F2B] cursor-pointer"
                          />
                          {c.name}
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-[#5C5555] mt-1">Pick all sections where this product should appear. First selected is the primary category.</p>
                </div>
                <label className="block"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Stock (shared)</span>
                  <input type="number" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})} data-testid="form-stock" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2"/>
                </label>
                <label className="block"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Price (₹)</span>
                  <input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} data-testid="form-price" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2"/>
                </label>
                <label className="block"><span className="text-xs uppercase tracking-wider text-[#5C5555]">MRP (₹, optional)</span>
                  <input type="number" value={form.mrp || ""} onChange={(e) => setForm({...form, mrp: e.target.value})} className="mt-1 w-full border border-[#E5DBD8] px-3 py-2"/>
                </label>
                <label className="sm:col-span-2"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Short Description</span>
                  <input value={form.short_description} onChange={(e) => setForm({...form, short_description: e.target.value})} className="mt-1 w-full border border-[#E5DBD8] px-3 py-2"/>
                </label>
                <label className="sm:col-span-2"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Full Description</span>
                  <textarea rows={4} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="form-description" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2"/>
                </label>
                <label className="sm:col-span-2"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Product Info (what&apos;s inside, sizes, ingredients, packaging)</span>
                  <textarea rows={4} value={form.product_info || ""} onChange={(e) => setForm({...form, product_info: e.target.value})} data-testid="form-product-info" placeholder="• Premium silk thread rakhi&#10;• Box of 250g kaju katli&#10;• Mixed dry fruits 200g&#10;• Hand-written greeting card" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2 font-mono text-sm"/>
                </label>
                <label className="sm:col-span-2"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Why this Hamper (special story / occasion fit / who it&apos;s for)</span>
                  <textarea rows={3} value={form.why_hamper || ""} onChange={(e) => setForm({...form, why_hamper: e.target.value})} data-testid="form-why-hamper" placeholder="Perfect for sisters who want to spoil their brothers with a complete celebration in a box…" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2"/>
                </label>
                <label className="sm:col-span-2"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Image URL</span>
                  <input value={form.images[0] || ""} onChange={(e) => setForm({...form, images: [e.target.value, ...(form.images || []).slice(1)]})} data-testid="form-image-url" placeholder="Paste image URL or upload below" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2"/>
                </label>
                <label className="sm:col-span-2"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Upload Images (select multiple · unlimited · max 5MB each · uploads in parallel)</span>
                  <input type="file" accept="image/*" multiple onChange={handleImageUpload} data-testid="form-image-upload" className="mt-1 w-full text-sm"/>
                  {formUploadProgress.total > 0 && (
                    <div className="mt-2 px-2.5 py-1.5 bg-[#FFF6F0] border border-dashed border-[#7A1F2B]/40 rounded" data-testid="form-image-progress">
                      <div className="flex items-center justify-between text-[11px] text-[#7A1F2B]">
                        <span className="font-semibold flex items-center gap-1.5">
                          <span className="inline-block w-2 h-2 rounded-full bg-[#7A1F2B] animate-pulse" />
                          Uploading {formUploadProgress.done} / {formUploadProgress.total}
                        </span>
                        <span className="text-[#5C5555]">Previews shown instantly, uploads finish in the background</span>
                      </div>
                      <div className="mt-1.5 h-1 bg-[#E5DBD8] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#7A1F2B] transition-all duration-300"
                          style={{ width: `${(formUploadProgress.done / Math.max(1, formUploadProgress.total)) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {(form.images || []).filter(Boolean).length > 0 && (
                    <div className="mt-2 flex items-center justify-between flex-wrap gap-2 px-2.5 py-1.5 bg-[#FFF6F0] border border-dashed border-[#7A1F2B]/30 rounded">
                      <p className="text-[11px] text-[#5C5555]">
                        <span className="font-semibold text-[#2D2424]">{(form.images || []).filter(Boolean).length}</span> image(s){" "}
                        {formImgSel.size > 0 && <span className="ml-1 text-[#7A1F2B]">· {formImgSel.size} selected</span>}
                        <span className="ml-2 text-[#9B9494]">· drag to reorder · #1 is the cover</span>
                      </p>
                      {formImgSel.size > 0 && (
                        <button
                          type="button"
                          onClick={bulkDeleteFormImages}
                          data-testid="form-image-bulk-delete"
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#7A1F2B] text-white text-[10px] uppercase tracking-wider rounded hover:bg-[#5C1520]"
                        >
                          <Trash2 className="w-3 h-3"/> Delete ({formImgSel.size})
                        </button>
                      )}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(form.images || []).filter(Boolean).map((img, i) => {
                      const isSelected = formImgSel.has(i);
                      const filled = (form.images || []).filter(Boolean).length;
                      const isDragging = formImgDrag && formImgDrag.from === i;
                      const isDropTarget = formImgDrag && formImgDrag.over === i && formImgDrag.from !== i;
                      return (
                        <div
                          key={i}
                          className={`relative w-20 h-20 group rounded transition-all ${isDropTarget ? "ring-2 ring-[#7A1F2B] scale-[1.06]" : ""} ${isDragging ? "opacity-40" : ""} ${isSelected ? "ring-2 ring-[#7A1F2B]" : ""}`}
                          draggable={filled > 1}
                          onDragStart={() => setFormImgDrag({ from: i, over: i })}
                          onDragEnter={(e) => { if (formImgDrag) { e.preventDefault(); setFormImgDrag((d) => ({ ...d, over: i })); } }}
                          onDragOver={(e) => { if (formImgDrag) e.preventDefault(); }}
                          onDrop={(e) => {
                            if (formImgDrag) {
                              e.preventDefault();
                              const from = formImgDrag.from;
                              setFormImgDrag(null);
                              if (from !== i) reorderFormImage(from, i);
                            }
                          }}
                          onDragEnd={() => setFormImgDrag(null)}
                        >
                          <img src={img} alt="" className="w-20 h-20 object-cover border border-[#E5DBD8] rounded pointer-events-none"/>
                          {/* Position badge */}
                          <span className="absolute top-0.5 left-0.5 px-1 py-0 rounded bg-white/95 border border-[#2D2424] text-[9px] font-doodle text-[#7A1F2B] leading-tight">#{i + 1}</span>
                          {/* Select checkbox */}
                          <label className="absolute bottom-0.5 left-0.5 inline-flex items-center justify-center w-4 h-4 rounded bg-white/95 border border-[#2D2424] cursor-pointer hover:bg-[#FFE7F1]" title="Select for bulk delete">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleFormImgSelected(i)}
                              data-testid={`form-image-select-${i}`}
                              className="w-3 h-3 accent-[#7A1F2B] cursor-pointer"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => removeImage(i)}
                            data-testid={`form-image-remove-${i}`}
                            className="absolute -top-2 -right-2 bg-[#7A1F2B] text-white w-5 h-5 rounded-full text-xs flex items-center justify-center"
                            aria-label="remove"
                          >×</button>
                        </div>
                      );
                    })}
                  </div>
                </label>
                <div className="sm:col-span-2 border border-[#E5DBD8] rounded p-3 bg-[#FFF6F0]" data-testid="form-variants-section">
                  <div className="mb-3 p-2.5 bg-white border border-[#E5DBD8] rounded">
                    <p className="text-[10px] uppercase tracking-wider text-[#2D2424] font-semibold mb-1.5">Default Colour (base product)</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="color"
                        value={form.color_hex || "#FFE7F1"}
                        onChange={(e) => setForm((f) => ({ ...f, color_hex: e.target.value }))}
                        data-testid="form-base-color-picker"
                        className="w-9 h-9 border border-[#E5DBD8] rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={form.color_hex || ""}
                        onChange={(e) => setForm((f) => ({ ...f, color_hex: e.target.value }))}
                        data-testid="form-base-color-hex"
                        placeholder="#RRGGBB"
                        className="w-24 border border-[#E5DBD8] px-2 py-1.5 text-xs font-mono"
                      />
                      <input
                        type="text"
                        value={form.color_name || ""}
                        onChange={(e) => setForm((f) => ({ ...f, color_name: e.target.value }))}
                        data-testid="form-base-color-name"
                        placeholder="Colour name (e.g. Ivory)"
                        className="flex-1 min-w-[10rem] border border-[#E5DBD8] px-2 py-1.5 text-sm"
                      />
                    </div>
                    <p className="text-[11px] text-[#5C5555] mt-1.5">Optional. Sets the colour label for the main product images above. Additional colours are added below and each appears as a separate product card in the shop.</p>
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#2D2424] font-semibold">Additional Colour Variants</p>
                      <p className="text-[11px] text-[#5C5555] mt-0.5">Each colour can override the product&apos;s content (short/long description, product info, why-this-hamper, tags), price and stock. Leave any field empty to inherit from the base product.</p>
                    </div>
                    <button
                      type="button"
                      onClick={addVariant}
                      data-testid="form-add-variant"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#7A1F2B] text-white text-xs uppercase tracking-wider rounded hover:bg-[#5C1520]"
                    >
                      <Plus className="w-3.5 h-3.5"/> Add colour
                    </button>
                  </div>

                  <div className="mt-3 space-y-3">
                    {(form.variants || []).map((v, vi) => (
                      <div key={v.id} className="bg-white border border-[#E5DBD8] rounded p-3 relative" data-testid={`form-variant-${vi}`}>
                        <button
                          type="button"
                          onClick={() => removeVariant(v.id)}
                          data-testid={`form-variant-remove-${vi}`}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full border border-[#E5DBD8] text-[#7A1F2B] flex items-center justify-center hover:bg-[#FFE7F1]"
                          aria-label="remove variant"
                        >
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 pr-8">
                          <label className="sm:col-span-2 block">
                            <span className="text-[10px] uppercase tracking-wider text-[#5C5555]">Swatch</span>
                            <div className="mt-1 flex items-center gap-2">
                              <input
                                type="color"
                                value={v.color_hex || "#FFE7F1"}
                                onChange={(e) => updateVariant(v.id, { color_hex: e.target.value })}
                                data-testid={`form-variant-color-picker-${vi}`}
                                className="w-9 h-9 border border-[#E5DBD8] rounded cursor-pointer"
                              />
                              <input
                                type="text"
                                value={v.color_hex || ""}
                                onChange={(e) => updateVariant(v.id, { color_hex: e.target.value })}
                                data-testid={`form-variant-color-hex-${vi}`}
                                placeholder="#RRGGBB"
                                className="w-24 border border-[#E5DBD8] px-2 py-1.5 text-xs font-mono"
                              />
                            </div>
                          </label>
                          <label className="sm:col-span-4 block">
                            <span className="text-[10px] uppercase tracking-wider text-[#5C5555]">Colour name *</span>
                            <input
                              type="text"
                              value={v.color_name || ""}
                              onChange={(e) => updateVariant(v.id, { color_name: e.target.value })}
                              data-testid={`form-variant-name-${vi}`}
                              placeholder="e.g. Blush Pink"
                              className="mt-1 w-full border border-[#E5DBD8] px-2 py-1.5 text-sm"
                            />
                          </label>
                          <label className="sm:col-span-3 block">
                            <span className="text-[10px] uppercase tracking-wider text-[#5C5555]">Price (₹, optional)</span>
                            <input
                              type="number"
                              min="0"
                              value={v.price ?? ""}
                              onChange={(e) => updateVariant(v.id, { price: e.target.value === "" ? null : Number(e.target.value) })}
                              data-testid={`form-variant-price-${vi}`}
                              placeholder="Falls back to base"
                              className="mt-1 w-full border border-[#E5DBD8] px-2 py-1.5 text-sm"
                            />
                          </label>
                          <label className="sm:col-span-3 block">
                            <span className="text-[10px] uppercase tracking-wider text-[#5C5555]">Stock</span>
                            <input
                              type="number"
                              min="0"
                              value={v.stock ?? 0}
                              onChange={(e) => updateVariant(v.id, { stock: parseInt(e.target.value || "0", 10) })}
                              data-testid={`form-variant-stock-${vi}`}
                              className="mt-1 w-full border border-[#E5DBD8] px-2 py-1.5 text-sm"
                            />
                          </label>
                          <label className="sm:col-span-12 block">
                            <span className="text-[10px] uppercase tracking-wider text-[#5C5555]">Short description for this colour (optional)</span>
                            <input
                              type="text"
                              value={v.short_description || ""}
                              onChange={(e) => updateVariant(v.id, { short_description: e.target.value })}
                              data-testid={`form-variant-short-desc-${vi}`}
                              placeholder="Tagline shown on the shop card. Leave empty to inherit."
                              className="mt-1 w-full border border-[#E5DBD8] px-2 py-1.5 text-sm"
                            />
                          </label>
                          <label className="sm:col-span-12 block">
                            <span className="text-[10px] uppercase tracking-wider text-[#5C5555]">Long description for this colour (optional)</span>
                            <textarea
                              rows={3}
                              value={v.description || ""}
                              onChange={(e) => updateVariant(v.id, { description: e.target.value })}
                              data-testid={`form-variant-description-${vi}`}
                              placeholder="Detailed description shown on the product page. Leave empty to inherit."
                              className="mt-1 w-full border border-[#E5DBD8] px-2 py-1.5 text-sm"
                            />
                          </label>
                          <label className="sm:col-span-6 block">
                            <span className="text-[10px] uppercase tracking-wider text-[#5C5555]">Product info for this colour (optional)</span>
                            <textarea
                              rows={2}
                              value={v.product_info || ""}
                              onChange={(e) => updateVariant(v.id, { product_info: e.target.value })}
                              data-testid={`form-variant-product-info-${vi}`}
                              placeholder="What&apos;s inside, sizes, ingredients — leave empty to inherit."
                              className="mt-1 w-full border border-[#E5DBD8] px-2 py-1.5 text-sm"
                            />
                          </label>
                          <label className="sm:col-span-6 block">
                            <span className="text-[10px] uppercase tracking-wider text-[#5C5555]">Why this hamper (this colour, optional)</span>
                            <textarea
                              rows={2}
                              value={v.why_hamper || ""}
                              onChange={(e) => updateVariant(v.id, { why_hamper: e.target.value })}
                              data-testid={`form-variant-why-hamper-${vi}`}
                              placeholder="Story / occasion fit / who it&apos;s for — leave empty to inherit."
                              className="mt-1 w-full border border-[#E5DBD8] px-2 py-1.5 text-sm"
                            />
                          </label>
                          <label className="sm:col-span-12 block">
                            <span className="text-[10px] uppercase tracking-wider text-[#5C5555]">Tags for this colour (optional, comma-separated)</span>
                            <input
                              type="text"
                              value={Array.isArray(v.tags) ? v.tags.join(", ") : (v.tags || "")}
                              onChange={(e) => updateVariant(v.id, { tags: e.target.value })}
                              data-testid={`form-variant-tags-${vi}`}
                              placeholder="e.g. bestseller, romantic — leave empty to inherit"
                              className="mt-1 w-full border border-[#E5DBD8] px-2 py-1.5 text-sm"
                            />
                          </label>
                          <label className="sm:col-span-12 block">
                            <span className="text-[10px] uppercase tracking-wider text-[#5C5555]">Images ({(v.images || []).length}/6)</span>
                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                              <label className={`inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#7A1F2B] text-white text-[10px] uppercase tracking-wider rounded cursor-pointer hover:bg-[#5C1520] ${(v.images || []).length >= 6 ? "opacity-50 cursor-not-allowed" : ""}`}>
                                <Upload className="w-3 h-3"/> Upload images
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  disabled={(v.images || []).length >= 6}
                                  className="hidden"
                                  data-testid={`form-variant-upload-${vi}`}
                                  onChange={(e) => { uploadVariantImages(v.id, e.target.files); e.target.value = ""; }}
                                />
                              </label>
                              {(v.images || []).length > 0 && (
                                <span className="text-[11px] text-[#5C5555]">First image is used as the shop-listing card cover.</span>
                              )}
                            </div>
                          </label>
                        </div>
                        {(v.images || []).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`form-variant-images-${vi}`}>
                            {(v.images || []).map((src, i) => (
                              <div key={i} className="relative w-14 h-14 rounded overflow-hidden border border-[#E5DBD8]">
                                <img src={src} alt="" className="w-full h-full object-cover"/>
                                <button
                                  type="button"
                                  onClick={() => removeVariantImage(v.id, i)}
                                  data-testid={`form-variant-image-remove-${vi}-${i}`}
                                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#7A1F2B] text-white text-[10px] flex items-center justify-center"
                                  aria-label="remove"
                                >×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {(form.variants || []).length === 0 && (
                      <p className="text-xs italic text-[#9B9494]">No additional colours yet. Click <b>Add colour</b> to offer this hamper in multiple colours — each will appear as a separate product card in the shop.</p>
                    )}
                  </div>
                </div>

                <label className="sm:col-span-2"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Tags (comma-separated)</span>
                  <input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} className="mt-1 w-full border border-[#E5DBD8] px-3 py-2"/>
                </label>
                <label className="sm:col-span-2 flex items-center gap-2">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} data-testid="form-featured"/>
                  <span className="text-sm">Featured on homepage</span>
                </label>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={cancel} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" data-testid="form-save" className="btn-primary flex-1">Save</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

